import { useSuspenseQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { maxRoutesQueryOptions } from '#api/routes.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { page, Titled } from '#util/title.ts';
import type { RouteFormData } from './route-schema.ts';
import { RouteSubscriptionForm } from './RouteSubscriptionForm.tsx';

type Props = {
  existingRoutes: Array<{ routeId: string }>;
  onSave: (data: RouteFormData) => void;
  onCancel: () => void;
};

export function AddRouteModal({ existingRoutes, onSave, onCancel }: Props) {
  const modalRef = useRef(null);
  const routeOptions = useSuspenseQuery({
    ...maxRoutesQueryOptions,
    select: (data) => ({
      items: data.data
        // Filter out Routes already existing in the RoutesList
        .filter(({ id }) => !existingRoutes.find(({ routeId }) => routeId === id))
        // Map the response to a useful shape
        .map((route) => ({ value: route.id, label: route.name })),
    }),
  }).data.items;

  return (
    <Modal isOpen={true} onClose={onCancel} ref={modalRef}>
      <Titled title={() => page('Add Route')}>
        <ModalHeader>Add Route</ModalHeader>
        <ModalBody>
          {routeOptions.length === 0 ?
            <p className="p-lg">All routes are added to this tag.</p>
          : <RouteSubscriptionForm onSave={onSave} routeOptions={routeOptions} />}
        </ModalBody>
        <ModalFooter>
          <ConfirmButtonGroup
            variant="right"
            primaryButton={
              routeOptions.length === 0 ?
                <Button onClick={onCancel}>Close</Button>
              : <Button type="submit" form="add-route">
                  Add
                </Button>
            }
            secondaryButton={
              routeOptions.length === 0 ?
                null
              : <Button variant="link" onClick={onCancel}>
                  Cancel
                </Button>
            }
          />
        </ModalFooter>
      </Titled>
    </Modal>
  );
}
